import clsx from "clsx";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { FC, useEffect, useRef } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { ChannelLink } from "@/features/channel/components/ChannelLink";
import { CommentTextarea } from "@/features/comment/components/CommentTextarea/CommentTextarea";
import { updateAfterComment } from "@/features/comment/helper";
import { Container } from "@/features/common/components/layout/Container";
import { useAnalytics } from "@/features/common/hooks/useAnalytics";
import { useFeatureEnabled } from "@/features/common/hooks/useFeatureEnabled";
import { PostActionBar } from "@/features/post/components/action/PostActionBar";
import { PostTag } from "@/features/post/components/common/PostTag";
import { PostBasicInformation } from "@/features/post/components/postDetail/components/PostBasicInformation";
import { PostTips } from "@/features/post/components/postDetail/components/PostTips";
import { scrollToSection } from "@/features/post/helper";
import { useIsPostLocked } from "@/features/post/hooks/useIsPostLocked";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  FeatureNameEnum,
  PostDetailFragment,
} from "@/generated/generated-hooks";

import { PostContent } from "./components/PostContent";
import { PostMedia } from "./components/PostMedia";

const CommentSection = dynamic(
  () =>
    import("@/features/comment/components/CommentSection/CommentSection").then(
      (mod) => mod.CommentSection,
    ),
  { ssr: false },
);

interface NormalPostDetailProps {
  post: PostDetailFragment;
}

export const NormalPostDetail: FC<NormalPostDetailProps> = ({ post }) => {
  const { t } = useTranslation("post");
  const router = useRouter();
  const { user } = useUserContext();
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const { isEnabled } = useFeatureEnabled();

  const isTipsEnable = isEnabled(FeatureNameEnum.Tips);

  useEffect(() => {
    if (router.asPath.includes("?reply")) {
      scrollToSection({
        anchor: "#comment-section",
        analyticsEnum: AnalyticsEventEnum.PostPageReplyUrlScrollToComment,
      });
    }
  }, [router.asPath]);

  const { writer, likes, title, _id, commentCount, channel, tags } = post;

  const isOwner = writer._id === user?._id;
  const isPreview = !!router.query.preview;
  const isPostLocked = useIsPostLocked(post);
  const canLeaveComment = channel.isCommentRestricted ? !isPostLocked : true;
  const { trackEvent } = useAnalytics();

  return (
    <div data-test-id="NormalPostContent-container">
      <div
        className={clsx(
          `w-full flex justify-center pb-6`,
          isPreview ? "pt-0" : "pt-6",
        )}
      >
        <div
          id="post-content"
          className="w-full flex flex-col max-w-3xl mb-6 space-y-4"
        >
          {/* Basic user information */}
          <Container>
            <PostBasicInformation post={post} />
          </Container>

          {/* Main Display Area */}
          <Container className="px-0 md:px-4">
            <PostMedia post={post} />
          </Container>

          <Container className="space-y-4 px-4">
            <div
              className="flex space-x-2 overflow-x-auto"
              ref={commentSectionRef}
            >
              <ChannelLink
                linkClassName="py-1"
                channel={post.channel}
                onClick={() => {
                  trackEvent(AnalyticsEventEnum.PostPageClickChannel, {
                    postId: _id,
                    channelSlug: post.channel.slug,
                    writerAccountName: writer.accountName,
                  });
                }}
              >
                <span
                  data-test-id="PostChannel-container"
                  className="px-6 py-1 bg-primary-secondary border border-solid border-primary rounded-3xl whitespace-nowrap text-sm font-light text-primary"
                >
                  {t("post:channel")}: &nbsp;
                  <span className="text-sm  text-primary">{channel.title}</span>
                </span>
              </ChannelLink>

              {tags.map((tag, idx) => (
                <PostTag
                  key={idx}
                  value={tag.value}
                  label={tag.label}
                  index={idx}
                />
              ))}
            </div>

            {/* Post Title */}
            <div
              className="text-2xl md:text-3xl font-bold"
              data-test-id="NormalPostContent-title"
            >
              {title}
            </div>

            <PostContent post={post} />

            {isTipsEnable && !isOwner && !!user && <PostTips post={post} />}

            <PostActionBar
              commentCount={commentCount}
              likes={likes}
              post={post}
              isPostOwner={isOwner}
            />

            {/* Comment Section */}
            <div id="comment-section">
              <CommentSection
                postId={_id}
                isPostOwner={isOwner}
                canLeaveComment={canLeaveComment}
              />
              <div className="block pb-4">
                <CommentTextarea
                  postId={_id}
                  update={updateAfterComment}
                  onUpdated={() => commentSectionRef.current.scrollIntoView()}
                  canLeaveComment={canLeaveComment}
                />
              </div>
            </div>
          </Container>
        </div>

        {/* @TODO:Implement in the future */}
        {/* SideBar */}
        {/* <View ml={isWideScreen ? 5 : 0} maxWidth="100%">
            <Sidebar />
          </View> */}
      </div>
    </div>
  );
};
